import { CartLinesQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { Divider, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import CartLinePrice from './CartLinePrice'
import CartLineQtyWidget from './CartLineQtyWidget'
import { useTranslation } from 'react-i18next'
import FormattedPrice from '@components/FormattedPrice'
import { getProductUrl } from '../../urls'

interface CartLinesProps {
  lines: CartLinesQueryResult['data']['cartLines']['results'][0][]
  lineLoading: number | null
  isLoading: boolean
  readonly: boolean

  handleEditQty(linePk: number, qty: number, variantPk: number): Promise<void>

  handleRemoveLine(linePk: number, variantPk: number): Promise<void>

  handleEditLine(linePk: number): Promise<void>

  handleApplySupplyToAll(supplyLineId: string): Promise<void>

  onClickProduct(specId: string | number, slug: string): void
}

const CartLines: React.FC<CartLinesProps> = ({
  lines,
  lineLoading,
  handleEditQty,
  handleRemoveLine,
  handleEditLine,
  handleApplySupplyToAll,
  onClickProduct,
  isLoading,
  readonly,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'))

  const handleClickProduct = (specId: number, slug: string) => e => {
    e.preventDefault()
    onClickProduct(specId, slug)
  }

  return (
    <>
      {smUp && (
        <>
          <div className='flex items-center py-6 px-12 uppercase text-gray leading-18 text-xs'>
            <span className='flex-1'>{t('item', 'Item')}</span>
            <span className='ml-12 w-90 text-right'>{t('total', 'Total')}</span>
            <span className='ml-12 w-100 text-center'>{t('qty', 'Qty')}</span>
          </div>
          <div className='hidden sm:block'>
            <Divider />
          </div>
        </>
      )}
      {!lines.length && (
        <div className='py-40 text-center text-lg relative'>
          {!isLoading && <span>{t('yourShoppingBagIsEmpty', 'Your Shopping Bag is empty.')}</span>}
        </div>
      )}
      <Loader
        className='spinner spinner--small'
        type='ThreeDots'
        color='#333'
        visible={isLoading}
        height={100}
        width={100}
      />
      <ul className='p-0 list-none'>
        {lines.map((line, idx) => {
          const variant = line.variant
          const product = variant.product
          const spec = product.siteSpecifics[0]
          const itemPrice = line.supplyParts.length
            ? line.supplyParts.reduce(
                (prev, curr) => {
                  return {
                    currency: curr.variant.wholesalePrice.currency,
                    undiscount: prev.undiscount + curr.variant.wholesalePrice.undiscount * curr.quantity,
                    discount: prev.discount + curr.variant.wholesalePrice.discount * curr.quantity,
                  }
                },
                { currency: '', undiscount: 0, discount: 0 }
              )
            : {
                currency: line.variant.wholesalePrice.currency,
                undiscount: line.variant.wholesalePrice.undiscount,
                discount: line.variant.wholesalePrice.discount,
              }
          const isSale = itemPrice.undiscount !== itemPrice.discount
          return (
            <li key={line.id} className='relative'>
              <Loader
                className='spinner spinner--small'
                type='ThreeDots'
                color='#333'
                visible={lineLoading === line.pk}
                height={100}
                width={100}
              />
              <div className='p-12 flex'>
                <Link
                  to={getProductUrl(spec?.slug, spec?.pk)}
                  onClick={handleClickProduct(spec?.pk, spec?.slug)}
                  className='mr-12 w-80 min-w-80 sm:w-120 sm:min-w-120'
                >
                  <img className='max-w-full' src={variant.thumbnailUrl || spec?.thumbnailUrl} alt={spec?.name} />
                </Link>
                <div className='flex-1'>
                  <div className='flex text-13 leading-18'>
                    <div className='w-full flex-1'>
                      <Link
                        to={getProductUrl(spec?.slug, spec?.pk)}
                        onClick={handleClickProduct(spec?.pk, spec?.slug)}
                        className='no-underline leading-6 break-words'
                      >
                        {spec?.shortName || spec?.name}
                      </Link>
                      <div className='mt-5 break-words'>
                        <span className='font-bold'>{t('sku', 'SKU')}: </span>
                        <span>{variant.sku}</span>
                      </div>
                      {variant.properties.map((prop, idx) => (
                        <div key={idx}>
                          <span className='font-bold'>{prop.attributeName}: </span>
                          <span>{prop.valueName}</span>
                        </div>
                      ))}
                      <div className='break-words'>
                        <span className='font-bold uppercase'>{t('productPrice', 'Product price')}: </span>
                        <span className={isSale ? 'line-through' : ''}>
                          <FormattedPrice amount={itemPrice.undiscount} currency={itemPrice.currency} />
                        </span>
                        {isSale && (
                          <span className={`text-red`}>
                            {' '}
                            <FormattedPrice amount={itemPrice.discount} currency={itemPrice.currency} />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='ml-12 w-auto sm:w-90 text-right flex flex-col mt-4'>
                      <CartLinePrice line={line} qty={line.quantity} />
                    </div>
                    {smUp && (
                      <div className='ml-12 w-100 text-center mt-4'>
                        {readonly ? (
                          <span>{line.quantity}</span>
                        ) : (
                          <CartLineQtyWidget
                            qty={line.quantity}
                            stockQty={variant.stockQuantity}
                            canEdit={variant.shippingRequired}
                            handleEditQty={qty => handleEditQty(line.pk, qty, line.variant.pk)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {!!line.supplyParts.length && (
                    <>
                      <div className='font-bold mt-10 flex justify-between items-center'>
                        <span>{t('kitsContent', "KIT's CONTENT")}:</span>
                      </div>
                      {line.supplyParts.map((part, idx) => {
                        const partSpec = part.variant?.product?.specifics[0]
                        return (
                          <div key={idx} className='text-sm'>
                            <span>{idx + 1}. </span>
                            <span>
                              <Link
                                to={getProductUrl(partSpec?.slug, partSpec?.pk)}
                                onClick={handleClickProduct(partSpec?.pk, partSpec?.slug)}
                                className='leading-6 break-words'
                              >
                                {partSpec?.name || 'N/A'}
                              </Link>
                            </span>
                            <span> {t('xQtyPcs', { defaultValue: 'x {{quantity}}pcs', quantity: part.quantity })}</span>
                            <span>
                              {' '}
                              - <CartLinePrice part={part} qty={1} />
                            </span>
                          </div>
                        )
                      })}
                    </>
                  )}
                  {smUp && !readonly && (
                    <div className='flex items-center justify-between -mx-8 mt-5'>
                      <div className='flex flex-1'>
                        {line.variant.isSupply && (
                          <>
                            {line.variant.isCustomizable && (
                              <span>
                                <Button
                                  className='transparent'
                                  onClick={() => handleEditLine(line.pk)}
                                  color='secondary'
                                  fullWidth
                                  disableRipple
                                >
                                  <span className='normal-case'>
                                    {t('button.customizeTheKit', 'Customize The KIT')}
                                  </span>
                                </Button>
                              </span>
                            )}
                            <span>
                              <Button
                                className='transparent'
                                onClick={() => handleApplySupplyToAll(line.id)}
                                color='secondary'
                                fullWidth
                                disableRipple
                              >
                                <span className='normal-case'>{t('button.applyToAllKits', 'Apply to all Kits')}</span>
                              </Button>
                            </span>
                          </>
                        )}
                      </div>

                      <span>
                        <Button
                          className='transparent'
                          onClick={() => handleRemoveLine(line.pk, line.variant.pk)}
                          color='warning'
                          fullWidth
                          disableRipple
                        >
                          <span className='normal-case'>{t('button.remove', 'Remove')}</span>
                        </Button>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {!smUp && !readonly && (
                <div className='flex items-center flex-wrap justify-between mt-5 pl-4 pr-12 pb-12'>
                  <div className='flex items-center'>
                    {line.variant.isSupply && (
                      <>
                        {line.variant.isCustomizable && (
                          <>
                            <span>
                              <Button
                                className='transparent'
                                onClick={() => handleEditLine(line.pk)}
                                color='secondary'
                                fullWidth
                                disableRipple
                              >
                                <span className='normal-case'>{t('button.customizeTheKit', 'Customize the KIT')}</span>
                              </Button>
                            </span>

                            <span>
                              <Button
                                className='transparent'
                                onClick={() => handleApplySupplyToAll(line.id)}
                                color='secondary'
                                fullWidth
                                disableRipple
                              >
                                <span className='normal-case'>{t('button.applyToAllKits', 'Apply to all Kits')}</span>
                              </Button>
                            </span>
                          </>
                        )}
                      </>
                    )}
                    <span>
                      <Button
                        className='transparent'
                        onClick={() => handleRemoveLine(line.pk, line.variant.pk)}
                        color='warning'
                        fullWidth
                        disableRipple
                      >
                        <span className='normal-case'>{t('button.remove', 'Remove')}</span>
                      </Button>
                    </span>
                  </div>
                  <div className='w-100 text-center'>
                    {!line.supplyParts.length && variant.shippingRequired && (
                      <CartLineQtyWidget
                        qty={line.quantity}
                        stockQty={variant.stockQuantity}
                        canEdit={!line.supplyParts.length && variant.shippingRequired}
                        handleEditQty={qty => handleEditQty(line.pk, qty, line.variant.pk)}
                      />
                    )}
                  </div>
                </div>
              )}
              {idx !== lines.length - 1 && <Divider />}
            </li>
          )
        })}
      </ul>
    </>
  )
}

export default CartLines
